/**
 * A box for a mark that is typed rather than picked: the chord symbol over a beat, the
 * figures under a bass note.
 *
 * Both are text, for the same reason. There are more chord symbols and more figured-bass
 * stacks than any palette could hold, and whoever writes them types them faster than they
 * could find them. So the box takes what was typed, hands it to the package unread, and
 * shows the sentence that comes back when it is not a mark: `Cmaj9` and `H7` fail in
 * different ways and the difference is worth seeing.
 *
 * The box is filled from the mark already there, which is how one is corrected rather than
 * only written, and it is re-synced when the selection moves -- the same way every other
 * box in this inspector follows the document.
 *
 * `askedAt` counts the times the score asked for the caret to come here, which is what ⌘K
 * and ⌘G do. A count rather than a flag: two presses are two requests, and a flag raised
 * once would answer only the first.
 */

import { useEffect, useRef, useState } from "react";
import { Field } from "./ScoreSetupFields";

const CONTROL =
  "h-10 w-full rounded-[10px] border border-rule bg-leaf px-2.5 text-[13px] text-ink outline-none focus:border-ink";

const BUTTON =
  "h-10 rounded-[10px] border border-rule px-3 text-[12px] text-ink-faded hover:border-ink-quiet hover:text-ink disabled:cursor-default disabled:text-ink-quiet/60";

export interface ScoreInspectorMarkProps {
  readonly label: string;
  readonly placeholder: string;
  /** The mark standing where the caret is, printed back as text, or `""` when there is none. */
  readonly standing: string;
  /** What the writing button says, since "Write the chord" and "the figure" are not the same. */
  readonly writeLabel: string;
  /** The line under the buttons saying how the text is written. */
  readonly help: string;
  /** Whether the panel around this is open: a hidden box cannot be given the caret. */
  readonly open: boolean;
  /** How many times the score has asked for the caret, rising by one each time. */
  readonly askedAt: number;
  /** Nothing is selected, so there is nowhere to put a mark. */
  readonly idle: boolean;
  write(text: string): void;
  remove(): void;
}

export default function ScoreInspectorMark({
  label,
  placeholder,
  standing,
  writeLabel,
  help,
  open,
  askedAt,
  idle,
  write,
  remove,
}: ScoreInspectorMarkProps): JSX.Element {
  const box = useRef<HTMLInputElement>(null);
  const answered = useRef(0);
  const [draft, setDraft] = useState(standing);
  useEffect(() => setDraft(standing), [standing]);

  // The request can arrive while the panel is shut, and the panel opens in the render after
  // that, so the answer waits for both. `answered` is what keeps opening the panel by hand
  // from stealing the caret off the score.
  useEffect(() => {
    if (!open || askedAt <= answered.current) return;
    answered.current = askedAt;
    box.current?.focus();
    box.current?.select();
  }, [askedAt, open]);

  const commit = (): void => {
    if (draft.trim() === "") return;
    write(draft);
  };

  return (
    <div className="space-y-2">
      <Field label={label}>
        <input
          ref={box}
          value={draft}
          placeholder={placeholder}
          spellCheck={false}
          disabled={idle}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") commit();
          }}
          className={CONTROL}
        />
      </Field>
      <div className="flex gap-2">
        <button type="button" className={BUTTON} disabled={idle} onClick={commit}>
          {writeLabel}
        </button>
        <button type="button" className={BUTTON} disabled={idle} onClick={remove}>
          Remove
        </button>
      </div>
      <p className="font-mono text-[11px] leading-relaxed text-ink-quiet">{help}</p>
    </div>
  );
}
